import React, { memo, useCallback, useMemo, useRef } from 'react';
import { connect } from 'react-redux';
import { getIsBot } from 'common/selectors/is-bot';
import { useOnScreen } from 'common/hooks/use-on-screen';
import { getReviewsCards, getReviewsRatingTotal } from 'features/reviews/selectors';
import { getRegionId } from 'features/page-model/ducks/get-model';
import { isMoscowRegion } from 'utils/is-moscow-region';
import { getOtzyvyUrl } from 'utils/get-otzyvy-url';
import { useIntersectionObserverCallback } from '@navi-toolbox/hooks';
import {
  getDisplayedName,
  getPrice,
  getProductId,
  getProductImages,
  getRawProductPriceCategory,
  getSource,
  isWebSellable,
} from 'features/product-info';
import { isWebAvailable as getIsWebAvailable } from 'features/goods-supply';
import { ConnectedProps } from 'types/utils';
import { analyticsUtils } from 'utils';
import { RootReducer } from 'store/root-reducer';
import { clickRRTracking, labelsGaNames, viewRRTracking } from './utils';
import { ProductCardView } from './product-card-view';
import { ProductCardPropsWithContainer, ProductUIElementsVisibility } from './types';

export type ProductLogicProps = {
  rrContentId?: string;
  elementsVisibility: ProductUIElementsVisibility;
} & ProductCardPropsWithContainer &
  ConnectedProps<typeof mapStateToProps>;

export type ProductLinkType = 'productNameLink' | 'productPhotoLink' | 'productRatingLink';

export type LabelType = 'STD' | 'LOF' | 'BPR' | 'PIM' | 'WIZ';

export const ProductCardLogic = memo<ProductLogicProps>(function ProductLogic(props) {
  const {
    rrContentId = '',

    elementsVisibility,
    Container,
    ImageContainer,
    productNameClassName,
    mainPriceClassName,
    fractPriceClassName,
    infoWrapperClassName,

    displayedName,
    label,
    price,
    productId,
    reviewsCount,
    reviewsScore,
    isBot,
    photoUrl,
    otzyvyUrl,
    isWebAvailable,
    isSellable,
  } = props;

  const isEligible = (isWebAvailable && isSellable) || isBot;

  const productRef = useRef<HTMLDivElement | null>(null);
  const isOnScreen = useOnScreen(productRef, {
    threshold: 1,
  });

  const commonGaData = useMemo(
    () => ({
      elementBlock: 'productReviews',
      elementType: 'product',
      productName: displayedName,
      productId: productId,
      productPrice: price?.main_price,
      productPrevPrice: price?.previous_price ?? null,
      productDiscount: price?.discount_percent ?? null,
      ...(label !== 'STD' && { productTag: labelsGaNames[label as LabelType] }),
    }),
    [displayedName, label, price?.main_price, price?.previous_price, price?.discount_percent, productId],
  );

  useIntersectionObserverCallback(
    productRef,
    {
      enter: () => {
        analyticsUtils.sendEvent('GA:impressions', {
          ...commonGaData,
        });
      },
      extraUnobserve: 'enter',
      options: { rootMargin: '-30px' },
    },
    [],
  );

  const onClick = useCallback(
    (elementName: ProductLinkType) => {
      analyticsUtils.sendEvent('GA:clickProduct', {
        elementName,
        ...commonGaData,
      });
    },
    [commonGaData],
  );

  const onMouseDown = (e: React.MouseEvent) => {
    if (
      e.ctrlKey ||
      e.shiftKey ||
      e.metaKey || // for apple
      (e.button && e.button === 1) // middle/wheel click
    ) {
      if (rrContentId) clickRRTracking(rrContentId);
      return;
    }

    // disallow right click
    if (e.button === 2) return;

    if (rrContentId) clickRRTracking(rrContentId);
  };

  if (isOnScreen && rrContentId) viewRRTracking(rrContentId);

  return (
    <ProductCardView
      ref={productRef}
      label={label}
      elementsVisibility={elementsVisibility}
      reviewsCount={reviewsCount}
      reviewsScore={reviewsScore}
      productUrl={'#'}
      isBot={isBot}
      Container={Container}
      ImageContainer={ImageContainer}
      productNameClassName={productNameClassName}
      mainPriceClassName={mainPriceClassName}
      fractPriceClassName={fractPriceClassName}
      infoWrapperClassName={infoWrapperClassName}
      displayedName={displayedName}
      price={price}
      productId={productId}
      photoUrl={photoUrl}
      otzyvyUrl={otzyvyUrl}
      onClick={onClick}
      onMouseDown={onMouseDown}
      isEligible={isEligible}
    />
  );
});

const mapStateToProps = (state: RootReducer) => {
  const reviewsCount = getReviewsCards(state)?.length || 0;
  const reviewsScore = getReviewsRatingTotal(state)?.score || 0;
  const isBot = getIsBot(state);
  const productId = getProductId(state) || '';
  const price = getPrice(state);
  const displayedName = getDisplayedName(state) || '';

  const source = getSource(state);
  const priceCategory = getRawProductPriceCategory(state);
  const label = (source?.toUpperCase() === 'PIM' ? 'PIM' : priceCategory) as LabelType;

  const images = getProductImages(state);
  const photoUrl = images?.[0].url || '';

  const regionCode = getRegionId(state);
  const isMoscow = isMoscowRegion(regionCode);
  const otzyvyUrl = getOtzyvyUrl(isMoscow);

  const isWebAvailable = getIsWebAvailable(state);
  const isSellable = isWebSellable(state);

  return {
    reviewsCount,
    reviewsScore,
    isBot,
    productId,
    price,
    displayedName,
    label,
    photoUrl,
    otzyvyUrl,
    isWebAvailable,
    isSellable,
  };
};

export const ProductCard = connect(mapStateToProps)(ProductCardLogic);
